/**
 * Hand off AI-prepared document drafts to Nouveau Document (DocumentCreation.js).
 * Reuses sessionStorage `documentConversionData` + ?convert=agent&type=…
 */

import i18n from '../../i18n';
import {
  resolveFacturationClient,
  resolveFacturationSupplier,
} from './agentDocumentBuilder';

const dp = (key, opts) => i18n.t(`modules.shared.aiAgent.documentHandoff.${key}`, opts);

export const AGENT_DOCUMENT_DRAFT_KEY = 'documentConversionData';

const SUPPLIER_DOC_TYPES = new Set([
  'quote-request',
  'proforma-request',
  'purchase-order',
  'return-slip',
  'payment-voucher',
]);

const DOC_TYPE_LABELS = {
  invoice: () => dp('types.invoice'),
  quote: () => dp('types.quote'),
  proforma: () => dp('types.proforma'),
  'purchase-order': () => dp('types.purchase-order'),
  'quote-request': () => dp('types.quote-request'),
  'proforma-request': () => dp('types.proforma-request'),
  'delivery-note': () => dp('types.delivery-note'),
  'payment-receipt': () => dp('types.payment-receipt'),
  'credit-note': () => dp('types.credit-note'),
  document: () => dp('types.document'),
};

export function normalizeAgentDocumentType(raw) {
  const value = String(raw || 'invoice')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');

  if (value === 'facture') return 'invoice';
  if (value === 'devis') return 'quote';
  if (value === 'bon-commande' || value === 'bon-de-commande') return 'purchase-order';
  if (value === 'delivery' || value === 'material-exit') return 'material-exit';
  if (value === 'credit-note' || value === 'credit_note') return 'credit-note';

  return value || 'invoice';
}

export function getDocumentCreationHandoffPath(docType, options = {}) {
  const normalized = normalizeAgentDocumentType(docType);
  const params = new URLSearchParams({
    convert: 'agent',
    type: normalized,
  });
  if (options.embed) {
    params.set('embed', 'agent');
  }
  return `/document-creation?${params.toString()}`;
}

export function getAgentDocumentTypeLabel(docType) {
  const normalized = normalizeAgentDocumentType(docType);
  const resolver = DOC_TYPE_LABELS[normalized];
  return resolver ? resolver() : dp('types.document');
}

function todayIsoDate() {
  return new Date().toISOString().split('T')[0];
}

function mapItemsToConversionFormat(rawItems, amountFallback = 0) {
  if (Array.isArray(rawItems) && rawItems.length > 0) {
    return rawItems.map((item, index) => {
      const quantity = Number(item.quantity) || 1;
      const price = Number(item.price ?? item.unit_price ?? item.unitPrice) || 0;
      const total = Number(item.total ?? item.subtotal) || quantity * price;
      return {
        id: item.id || Date.now() + index,
        product: item.name || item.product || item.productName || dp('service_line'),
        productId: item.productId || null,
        quantity,
        price,
        total,
        description: item.description || '',
        unit: item.unit || '',
      };
    });
  }

  const amount = Number(amountFallback) || 0;
  if (amount <= 0) {
    return [
      {
        id: Date.now(),
        product: dp('service_line'),
        quantity: 1,
        price: 0,
        total: 0,
        unit: 'pcs',
      },
    ];
  }

  return [
    {
      id: Date.now(),
      product: dp('service_line'),
      quantity: 1,
      price: amount,
      total: amount,
      unit: 'pcs',
    },
  ];
}

export async function buildAgentDocumentConversionData(params = {}) {
  const docType = normalizeAgentDocumentType(params.type || params.documentType || 'invoice');
  const isSupplierDoc = SUPPLIER_DOC_TYPES.has(docType);

  const partnerName =
    params.client ||
    params.clientName ||
    params.supplier ||
    params.supplierName ||
    params.name ||
    '';

  let partnerId = null;
  let partnerLabel = String(partnerName || dp('partner_fallback')).trim() || dp('partner_fallback');

  if (isSupplierDoc) {
    const resolved = await resolveFacturationSupplier(partnerName);
    partnerId = resolved.supplierId;
    partnerLabel = resolved.supplierLabel;
  } else {
    const resolved = await resolveFacturationClient(partnerName);
    partnerId = resolved.clientId;
    partnerLabel = resolved.clientLabel;
  }

  const items = mapItemsToConversionFormat(params.items, params.amount);
  const subtotal = items.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

  const conversionData = {
    source: 'agent',
    date: params.date || todayIsoDate(),
    notes:
      params.notes ||
      dp('default_notes'),
    items,
    amount: subtotal,
    subtotal,
  };

  if (params.dueDate || params.due_date) {
    conversionData.dueDate = params.dueDate || params.due_date;
  }
  if (params.title) {
    conversionData.title = params.title;
  }
  if (params.paymentMethod) {
    conversionData.paymentMethod = params.paymentMethod;
  }

  if (isSupplierDoc) {
    conversionData.supplier = partnerId || '';
    conversionData.supplierName = partnerLabel;
  } else {
    conversionData.client = partnerId || '';
    conversionData.clientName = partnerLabel;
  }

  return { docType, conversionData };
}

export async function prepareAgentDocumentHandoff(params = {}) {
  const { docType, conversionData } = await buildAgentDocumentConversionData(params);

  try {
    sessionStorage.setItem(AGENT_DOCUMENT_DRAFT_KEY, JSON.stringify(conversionData));
  } catch (error) {
    console.error('Agent document handoff: sessionStorage failed', error);
    return {
      success: false,
      error: dp('handoff_failed'),
    };
  }

  return {
    success: true,
    docType,
    openPath: getDocumentCreationHandoffPath(docType, { embed: true }),
    fullPagePath: getDocumentCreationHandoffPath(docType),
    conversionData,
  };
}
